import Container from '@amzn/awsui-components-react-v3/polaris/container';
import { AppLayout, Flash, Tabs } from 'AWS-UI-Components-React';
import React from 'react';
import { getAccess, isFinanceReadOnly } from '../../utils/access-utils';
import { getJSClient } from '../js_client';
import { AccuracyInfo } from '../mapper/accuracy_benchmark_info';
import { BcmBranchSelector } from '../mapper/bcm_branch_selector';
import { MapperForm } from '../mapper/mapper_form';
import { BenchmarkAdjustmentContainer } from './benchmark_adjustment/benchmark_adjustment_container';
import { ChannelMapperContainer } from './channel_mapping/channel_mapping_container';
import { CognosConfigContainer } from './cognos_config';
import { DevConfigContainer } from './dev_config';
import { GLPLMapperContainer } from './gl_pl_mapping/gl_pl_container';
import { PLGLMapperContainer } from './pl_gl_mapping/container';
import { deleteFromLocalStorage, LOCAL_STORAGE_KEYS } from '../../utils/local_storage';

export class ConfigCentralContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      configReady: false,
      activeTabId: 'cognos',
      toolsContent: <AccuracyInfo />,
      toolsOpen: false,
      selectedBranchId: null,
      stagingArea: props.identity.username,
    };
  }

  componentWillMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
    document.title = 'Phoenix : Config Central';
  };

  initialClientTasks = () => {
    this.setState({ readOnly: true });
    this.getUserIdentity();
    this.client.getConfig(this.onConfigFetchSuccess);
  };

  getUserIdentity = () => {
    this.setState({ readOnly: isFinanceReadOnly(this.props.identity), access: getAccess(this.props.identity) });
    console.log(`User access: ${this.props.identity.access}`);
  };

  onConfigFetchSuccess = (
    response,
    configKeys = ['componentConfig', 'marketplaceConfig', 'etlConfig', 'cognosConfig'],
    resetConfig = true
  ) => {
    const configStates = configKeys.reduce((map, key) => {
      map[key] = JSON.parse(response[key]);
      return map;
    }, {});

    const resetStates = resetConfig ? { configReady: true, key: new Date().toUTCString() } : {};

    try {
      this.setState({
                      ...resetStates,
                      ...configStates,
                    });
    } catch (e) {
      console.log(e);
    }
  };

  onMappingPublish = () => {
    // When an updated mapping is published - we want to reset config data in cache
    // so that other views will not be using the older config
    deleteFromLocalStorage(LOCAL_STORAGE_KEYS.CONFIG);

    // Update only the cognos config objects in state, resetting all items will cause the whole
    // component to refresh and cause the user selection (selected component, marketplace, etc)
    // to be lost
    this.client.getConfig((response) => this.onConfigFetchSuccess(response, ['cognosConfig'], false));
  };

  onBranchChange = (branchId) => {
    const stagingArea = branchId || this.props.identity.username;
    this.setState({ selectedBranchId: branchId, stagingArea });
  };

  getTabs = () => {
    const mapperTab = {
      label: 'Accuracy Mappings',
      id: 'mapper',
      content: (
        <AppLayout
          className="awsui-util-no-gutters"
          content={
            <MapperForm
              cognosConfig={this.state.cognosConfig}
              etlConfig={this.state.etlConfig}
              marketplaceConfig={this.state.marketplaceConfig}
              componentConfig={this.state.componentConfig}
              identity={this.props.identity}
              featureList={ this.props.featureList }
              readOnly={this.state.readOnly}
              onMappingPublish={() => this.onMappingPublish()}
              updateTools={ (toolsContentNew) => {
                this.setState({ toolsContent: toolsContentNew, toolsOpen: true });
              }}
              updateToolsContent={ (toolsContentNew) => {
                this.setState({ toolsContent: toolsContentNew });
              }}
            />
          }
          navigationHide={ true }
          tools={ this.state.toolsContent }
          toolsOpen={ this.state.toolsOpen }
          onToolsChange={ ({detail}) => this.state.toolsOpen = detail.open }
        />
      )
    };

    const cognosConfigTab = {
      label: 'Cognos Config',
      id: 'cognos',
      content: (
        <Container>
            <CognosConfigContainer
              cognosConfig={this.state.cognosConfig}
              etlConfig={this.state.etlConfig}
              marketplaceConfig={this.state.marketplaceConfig}
              componentConfig={this.state.componentConfig}
              identity={this.props.identity}
              readOnly={this.state.readOnly}
            />
        </Container>
      ),
    };

    const plGlConfigTab = {
      label: 'PL-GL Mapper',
      id: 'plgl',
      content: <PLGLMapperContainer user={this.props.identity.username} readOnly={this.state.readOnly} />,
    };

    const glPlConfigTab = {
      label: 'GL-PL Mapper',
      id: 'glpl',
      content: <GLPLMapperContainer user={this.props.identity.username} readOnly={this.state.readOnly} />,
    };

    const channelMappingsTab = {
      label: 'Channel Mappings',
      id: 'channelMappings',
      content: (
        <ChannelMapperContainer
          user={this.props.identity.username}
          readOnly={this.state.readOnly}
          marketplaceConfig={this.state.marketplaceConfig}
        />
      ),
    };

    const devConfigTab = {
      label: 'Developer Config',
      id: 'dev',
      content: (
        <Container>
          <DevConfigContainer
            cognosConfig={this.state.cognosConfig}
            appConfig={this.props.appConfig}
            setAppConfig={this.props.setAppConfig}
          />
        </Container>
      ),
    };

    const benchmarkAdjustTab = {
      label: 'Benchmark Adjustment',
      id: 'benchmarkAdjustment',
      content: (
        <BenchmarkAdjustmentContainer
          cognosConfig={this.state.cognosConfig}
          etlConfig={this.state.etlConfig}
          marketplaceConfig={this.state.marketplaceConfig}
          user={this.props.identity.username}
        />
      ),
    };

    const configCentralFeaturesTabMap = {
      "mapper": mapperTab,
      "cognosConfig" : cognosConfigTab,
      "plglConfig" : plGlConfigTab,
      "glplConfig" : glPlConfigTab,
      "channel-mappings" : channelMappingsTab,
      "benchmarkAdjustment" : benchmarkAdjustTab,
      "devConfig": devConfigTab
    };

    let tabList = [];
    Object.keys(configCentralFeaturesTabMap)
          .filter((k) => this.props.featureList.includes(k))
          .forEach((k) => tabList.push(configCentralFeaturesTabMap[k]));
    return tabList;
  };

  onTabChange = (event) => {
    let activeTabId = event.detail.activeTabId;
    if (activeTabId) {
      this.setState({ activeTabId });
    }
  };

  render() {
    return (
      <div>
        {this.state.configReady && (
          <Tabs
            className="awsui-util-no-gutters"
            key={this.state.key}
            tabs={this.getTabs()}
            activeTabId={this.state.activeTabId}
            onChange={this.onTabChange}
          />
        )}
        {!this.state.configReady && <Flash loading content="Loading configurations from server" />}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/config_central_container.jsx